import { useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { X, AlertTriangle, Loader2 } from "lucide-react";

/**
 * DeleteAccountModal — GDPR-compliant soft-delete of a user's own account.
 * Requires the user to re-type their email as a confirmation step.
 */
export default function DeleteAccountModal({ email, onClose, onDeleted }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const del = async () => {
    setBusy(true);
    try {
      await api.post("/me/delete-account", { confirm: confirm.trim() });
      toast.success("Account deleted. Your personal data has been anonymized.");
      onDeleted?.();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const matches = confirm.trim().toLowerCase() === (email || "").toLowerCase();

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="delete-modal">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-[color:var(--mano-border)]">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-red-100 text-red-700 flex items-center justify-center">
              <AlertTriangle size={18} />
            </div>
            <div>
              <div className="font-display text-lg">Delete account</div>
              <div className="uppercase-label">This action is permanent</div>
            </div>
          </div>
          <button
            data-testid="delete-close"
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[color:var(--mano-border)]/40"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-[color:var(--mano-ink)]">
            We'll <b>anonymize your personal profile immediately</b> (name, email, phone,
            date of birth). Clinical records like screening reports and encounter notes are
            kept in line with medical record-keeping obligations, but they will no longer
            be linked to you personally.
          </p>
          <p className="text-sm text-[color:var(--mano-ink-soft)]">
            To confirm, type your account email below:
            <br />
            <span className="font-mono text-[color:var(--mano-ink)]">{email}</span>
          </p>
          <input
            data-testid="delete-confirm-input"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Type your email to confirm"
            className="w-full h-11 rounded-xl border border-[color:var(--mano-border)] bg-white px-3 text-sm"
          />
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              data-testid="delete-cancel"
              onClick={onClose}
              className="px-4 py-2 rounded-full border border-[color:var(--mano-border)] text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              data-testid="delete-confirm-btn"
              onClick={del}
              disabled={!matches || busy}
              className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-700 text-white font-semibold text-sm disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
              Permanently delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
